import { ConsentManagementConfigVersion } from "@/api/consent-management";
import {
  consentManagerConfigSchema,
  initialValues,
} from "@/consent-management/components/ConfigPage/ConsentManagerConfigForm/schema";
import { diffConsentConfigs } from "@/consent-management/components/ConfigPage/ConsentManagerConfigForm/utils";
import { DestinationConfigSaasDestinationPair } from "@/hooks/useDestinationConfigSaasDestinationPairs";

export function withConsentCategory(
  draft: ConsentManagementConfigVersion,
  destinationKey: string,
  category: string,
): ConsentManagementConfigVersion {
  return {
    ...draft,
    consentMapping: { ...draft.consentMapping, [destinationKey]: category },
  };
}

/**
 * Mirrors the Consent Manager page's publish check: the draft as that page's
 * form would load it must pass the form schema.
 */
export function isConsentDraftPublishable({
  draft,
  enabledCategorizableDestinations,
  audiencesEnabled,
  sessionReplayEntitled,
}: {
  draft: ConsentManagementConfigVersion;
  enabledCategorizableDestinations: DestinationConfigSaasDestinationPair[];
  audiencesEnabled: boolean;
  sessionReplayEntitled: boolean;
}): boolean {
  const formValues = initialValues(
    draft,
    enabledCategorizableDestinations,
    audiencesEnabled,
    sessionReplayEntitled,
    true,
  );
  return consentManagerConfigSchema.safeParse(formValues).success;
}

/** Whether the draft differs from the published version beyond this destination's category. */
export function hasOtherUnpublishedChanges({
  draft,
  lastPublished,
  destinationKey,
}: {
  draft: ConsentManagementConfigVersion;
  lastPublished: ConsentManagementConfigVersion | undefined;
  destinationKey: string;
}): boolean {
  if (!lastPublished) {
    return false;
  }
  const { [destinationKey]: _draftCategory, ...otherMappings } =
    draft.consentMapping;
  const publishedCategory = lastPublished.consentMapping[destinationKey];
  const comparable = {
    ...draft,
    consentMapping:
      publishedCategory === undefined
        ? otherMappings
        : { ...otherMappings, [destinationKey]: publishedCategory },
  };
  return (
    diffConsentConfigs({ config1: lastPublished, config2: comparable }).length >
    0
  );
}
